import type { ComponentType, ReactNode } from 'react';
import { useFeature } from '@hero/web-context';
import type { FeatureFlagKey, SubFlagKeysOf } from '@hero/core';
import { ComingSoonScreen } from './ComingSoonScreen';
import { MaintenanceScreen } from './MaintenanceScreen';
import { DisabledScreen } from './DisabledScreen';

export interface FeatureProps<
  K extends FeatureFlagKey = FeatureFlagKey,
  P extends Record<string, unknown> = Record<string, never>,
> {
  flag: K;
  subFlag?: SubFlagKeysOf<K>;
  component: ComponentType<P>;
  props?: P;
  icon?: ComponentType<{ className?: string }>;
  comingSoon?: ReactNode;
  maintenance?: ReactNode;
  disabled?: ReactNode;
}

export function Feature<
  K extends FeatureFlagKey,
  P extends Record<string, unknown> = Record<string, never>,
>({
  flag,
  subFlag,
  component: Component,
  props,
  icon,
  comingSoon,
  maintenance,
  disabled,
}: FeatureProps<K, P>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { isEnabled, status } = useFeature(flag, subFlag as any);

  if (status === 'coming_soon') return <>{comingSoon ?? <ComingSoonScreen icon={icon} />}</>;
  if (status === 'hidden') return null;
  if (status === 'maintenance') return <>{maintenance ?? <MaintenanceScreen icon={icon} />}</>;
  if (status === 'disabled' || !isEnabled) return <>{disabled ?? <DisabledScreen icon={icon} />}</>;

  return <Component {...(props as P)} />;
}
